/**
 * Modules (O2C, H2R): the product's two processes, and the URL as their home.
 *
 * The current module lives in the `module` search param so a link, a reload and
 * the back button all keep it. Pure functions: no React, no fetching — the hook
 * that reads the router is `useModule.ts`. Mirrors the backend's
 * `config/modules.ts`; the wire value is always the code, never an id.
 */

export const MODULE_CODES = ['O2C', 'H2R'] as const;

export type ModuleCode = (typeof MODULE_CODES)[number];

/** What an absent or unknown `module` means: how the product behaved before
 *  modules existed. */
export const DEFAULT_MODULE: ModuleCode = 'O2C';

export const MODULE_LABELS: Record<ModuleCode, string> = {
	O2C: 'Order to Cash',
	H2R: 'Hire to Retire',
};

export const MODULE_PARAM = 'module';

export function isModuleCode(value: unknown): value is ModuleCode {
	return (
		typeof value === 'string' &&
		(MODULE_CODES as readonly string[]).includes(value)
	);
}

/** Case-insensitive; absent or unknown falls back to O2C. */
export function parseModule(value: string | null | undefined): ModuleCode {
	const upper = value?.trim().toUpperCase();
	return isModuleCode(upper) ? upper : DEFAULT_MODULE;
}

/** The module a URL's search string names (`?module=h2r` or a `URLSearchParams`). */
export function readModule(search: URLSearchParams | string): ModuleCode {
	const params =
		typeof search === 'string' ? new URLSearchParams(search) : search;
	return parseModule(params.get(MODULE_PARAM));
}

/** A copy of `params` with `module` set and every other param kept. */
export function setModuleParam(
	params: URLSearchParams,
	module: ModuleCode,
): URLSearchParams {
	const next = new URLSearchParams(params);
	next.set(MODULE_PARAM, module);
	return next;
}

/**
 * Everything under one client in one module lives below this path, the module
 * in lower case: `/clients/3/modules/h2r` is the hub, `…/data-files` and
 * `…/audits` its two halves.
 */
export function clientModuleBase(clientId: string, module: ModuleCode): string {
	return `/clients/${clientId}/modules/${module.toLowerCase()}`;
}

/* The client list and everything under `/clients/:id/modules` never carry
   `?module=`: the list and the module page belong to no module, and every
   page below them names its module in the path. */
const MODULE_FREE_PATH = /^\/clients(\/[^/]+\/modules(\/.*)?)?\/?$/;

/** True for a path that must not get `?module=` (see above). */
export function isModuleFreePath(pathname: string): boolean {
	return MODULE_FREE_PATH.test(pathname);
}

const INTAKE_PATH = /^\/intake\/clients\/([^/]+)(\/.*)?$/;
const AUDITS_PATH = /^\/audits(\/.*)?$/;

/**
 * The client/module path for an old-style one, or null when there is none:
 *
 *   /intake/clients/3              → /clients/3/modules/o2c/data-files
 *   /intake/clients/3/upload       → …/data-files/upload
 *   /intake/clients/3/files/9/map… → …/data-files/9/map…
 *   /audits?client=3, /audits/new  → …/audits, …/audits/new
 *   /audits/12/analyses            → …/audits/12/analyses
 *
 * An audits path needs the client: `?client=` (dropped from `params`) or
 * `clientId`. Without either it has no client path.
 */
function clientModulePath(
	base: string,
	params: URLSearchParams,
	module: ModuleCode,
	clientId?: string,
): string | null {
	const intake = INTAKE_PATH.exec(base);
	if (intake) {
		const rest = (intake[2] ?? '')
			.replace(/^\/files(?=\/)/, '')
			.replace(/^\/file-groups$/, '');
		return `${clientModuleBase(intake[1] ?? '', module)}/data-files${rest === '/' ? '' : rest}`;
	}
	const audits = AUDITS_PATH.exec(base);
	const client = params.get('client') || clientId;
	if (audits && client) {
		params.delete('client');
		return `${clientModuleBase(client, module)}/audits${audits[1] ?? ''}`;
	}
	return null;
}

/**
 * `path` with `module=` added or replaced, for a link. The path may already
 * carry a query string and/or a hash; both are kept. O2C is written out too,
 * so a link never depends on the default.
 *
 * An old-style intake or audits path is moved under its client and module
 * (`clientModulePath` above; `clientId` is the client an audit belongs to
 * when the path does not say), and a module-free path gets no `module=`.
 */
export function withModule(
	path: string,
	module: ModuleCode,
	clientId?: string,
): string {
	const hashAt = path.indexOf('#');
	const hash = hashAt === -1 ? '' : path.slice(hashAt);
	const beforeHash = hashAt === -1 ? path : path.slice(0, hashAt);
	const queryAt = beforeHash.indexOf('?');
	const query = queryAt === -1 ? '' : beforeHash.slice(queryAt + 1);
	const params = new URLSearchParams(query);
	const given = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
	const base = clientModulePath(given, params, module, clientId) ?? given;
	if (isModuleFreePath(base)) {
		params.delete(MODULE_PARAM);
		const rest = params.toString();
		return `${base}${rest ? `?${rest}` : ''}${hash}`;
	}
	return `${base}?${setModuleParam(params, module).toString()}${hash}`;
}

/** One line under each module's name on the selector cards. */
export const MODULE_BLURBS: Record<ModuleCode, string> = {
	O2C: 'Sales and returns registers',
	H2R: 'Employee master, payroll and attendance',
};

/* Params that name something inside one module — a file group, a folder of
   it. Carried across a module switch they would point at nothing. */
const MODULE_SCOPED_PARAMS = ['group', 'folder'] as const;

/**
 * A copy of `params` for a switch to `module`: `module` set, the selected
 * group and folder dropped (a group belongs to one module), the rest kept.
 */
export function switchModuleParams(
	params: URLSearchParams,
	module: ModuleCode,
): URLSearchParams {
	const next = setModuleParam(params, module);
	for (const name of MODULE_SCOPED_PARAMS) next.delete(name);
	return next;
}

/**
 * Where an arrow key moves the selection in a radio group of `count`
 * options, from `index`: right/down forward, left/up back, wrapping; Home
 * and End to the ends. `null` for any other key.
 */
export function radioKeyTarget(
	index: number,
	key: string,
	count: number,
): number | null {
	if (count <= 0) return null;
	switch (key) {
		case 'ArrowRight':
		case 'ArrowDown':
			return (index + 1) % count;
		case 'ArrowLeft':
		case 'ArrowUp':
			return (index - 1 + count) % count;
		case 'Home':
			return 0;
		case 'End':
			return count - 1;
		default:
			return null;
	}
}
